import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { environment } from '../app/app.environment';
import { CounterSession, User } from '../modals/user.model';
import { ClientTemplateService } from './client-template.service';
import { IndustryService } from './industry.service';
import { AccountRegistryService } from './account-registry.service';
import { DynamicTemplateConfigService } from './pdf-generation/dynamic-template-config.service';

export interface SessionStatus {
  isWithinSession: boolean;
  minutesRemaining: number;
  sessionEndTime: Date | null;
  jwtExpiresBeforeSession: boolean;
  jwtMinutesRemaining: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<any>;
  private jwtHelper: JwtHelperService;
  apiUrl = environment.apiURL + 'v1';

  private sessionWarningSubject = new BehaviorSubject<string>('');
  public sessionWarning$ = this.sessionWarningSubject.asObservable();

  constructor(
    private http: HttpClient,
    private clientTemplateService: ClientTemplateService,
    private industryService: IndustryService,
    private dynamicTemplateConfigService: DynamicTemplateConfigService,
    private accountRegistry: AccountRegistryService,
  ) {
    this.jwtHelper = new JwtHelperService();
    const name = localStorage.getItem('name') || '{}';
    this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(name));
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  public verifyOTP(username: string, otp: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/verifyOtp`, {
      username,
      otp,
    });
  }

  public setUserValue(user: User) {
    // FIX: Use .next() instead of replacing the BehaviorSubject.
    // Replacing the subject orphans all existing subscribers (e.g. IndustryUiConfigService),
    // which caused Mobile industry UI to not apply on first login without a page refresh.
    this.currentUserSubject.next(user);
    // OLD (kept for reference — do not re-enable):
    // this.currentUserSubject = new BehaviorSubject<User>(user);
    // this.currentUser = this.currentUserSubject.asObservable();
  }

  public sendOTP(username: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/sendOtp`, { username });
  }

  public resetPassword(payload: {
    username: string;
    otp: string;
    newPassword: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/resetPassword`, payload);
  }

  public isAuthenticated(): boolean {
    const user: User = JSON.parse(localStorage.getItem('name') || 'null');
    if (user) {
      return !this.jwtHelper.isTokenExpired(user.accessToken || '');
    }
    return false;
  }

  storeCredentials(username: string, password: string): void {
    const credentials = btoa(`${username}:${password}`);
    localStorage.setItem('auth_credentials', credentials);
  }

  getStoredCredentials(): string | null {
    return localStorage.getItem('auth_credentials');
  }

  getCounterSession(): CounterSession | null {
    const user = this.currentUserValue;
    return user?.countersession || null;
  }

  private parseTimeToToday(timeStr: string): Date {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    const today = new Date();
    today.setHours(hours, minutes, seconds, 0);
    return today;
  }

  getSessionStatus(): SessionStatus {
    const session = this.getCounterSession();
    const now = new Date();

    if (!session) {
      return {
        isWithinSession: false,
        minutesRemaining: 0,
        sessionEndTime: null,
        jwtExpiresBeforeSession: false,
        jwtMinutesRemaining: 0,
      };
    }

    const sessionEnd = this.parseTimeToToday(session.end_time);
    const sessionStart = this.parseTimeToToday(session.start_time);

    if (sessionEnd < sessionStart) {
      sessionEnd.setDate(sessionEnd.getDate() + 1);
    }

    const isWithinSession = now >= sessionStart && now <= sessionEnd;
    const minutesRemaining = Math.max(
      0,
      Math.floor((sessionEnd.getTime() - now.getTime()) / 60000),
    );

    const jwtMinutesRemaining = this.getJwtMinutesRemaining();
    const jwtExpiresBeforeSession =
      jwtMinutesRemaining < minutesRemaining && jwtMinutesRemaining > 0;

    return {
      isWithinSession,
      minutesRemaining,
      sessionEndTime: sessionEnd,
      jwtExpiresBeforeSession,
      jwtMinutesRemaining,
    };
  }

  getJwtMinutesRemaining(): number {
    try {
      const user = this.currentUserValue;
      if (!user?.accessToken) return 0;

      const decodedToken = this.jwtHelper.decodeToken(user.accessToken);
      if (!decodedToken?.exp) return 0;

      const expDate = new Date(decodedToken.exp * 1000);
      const now = new Date();
      const minutesRemaining = Math.max(
        0,
        Math.floor((expDate.getTime() - now.getTime()) / 60000),
      );

      return minutesRemaining;
    } catch {
      return 0;
    }
  }

  getSessionDurationMinutes(): number {
    const session = this.getCounterSession();
    if (!session) return 0;

    const sessionEnd = this.parseTimeToToday(session.end_time);
    const sessionStart = this.parseTimeToToday(session.start_time);

    if (sessionEnd < sessionStart) {
      sessionEnd.setDate(sessionEnd.getDate() + 1);
    }

    return Math.floor((sessionEnd.getTime() - sessionStart.getTime()) / 60000);
  }

  refreshToken(): Observable<any> {
    const credentials = this.getStoredCredentials();
    if (!credentials) {
      return of({ error: 'No credentials stored' });
    }

    const headers = new HttpHeaders({
      Authorization: `Basic ${credentials}`,
    });

    return this.http
      .get<any>(`${this.apiUrl}/auth/refreshToken`, { headers })
      .pipe(
        tap((response) => {
          if (response.accessToken) {
            localStorage.setItem('name', JSON.stringify(response));
            this.currentUserSubject.next(response);
          }
        }),
        catchError((error) => {
          console.error('Token refresh failed:', error);
          return of({ error: 'Refresh failed' });
        }),
      );
  }

  setSessionWarning(message: string): void {
    this.sessionWarningSubject.next(message);
  }

  clearSessionWarning(): void {
    this.sessionWarningSubject.next('');
  }

  login(
    username: string,
    password?: string,
    warehouseId?: string,
  ): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/auth/login`, {
        username: username,
        password: password,
        warehouseId: warehouseId,
      })
      .pipe(
        tap((user) => {
          console.log('Login successful, user data:', user);
          localStorage.setItem('name', JSON.stringify(user));
          localStorage.setItem('storename', JSON.stringify(user.warehouse));
          localStorage.setItem('client_id', JSON.stringify(user.client_id));
          this.loadClientSaleData(user.client_id);

          // Store industry from login response
          if (user.industry) {
            console.log('Setting industry from login response:', user.industry);
            this.industryService.setIndustry(user.industry);
          }

          if (password) {
            this.storeCredentials(username, password);
          }
          this.accountRegistry.registerActive(username);

          this.currentUserSubject.next(user);
        }),
        switchMap((user) => {
          // Load client templates after successful login; a template error must not fail an already-stored login
          if (user.client_id) {
            return this.clientTemplateService
              .getClientTemplates(user.client_id)
              .pipe(
                tap(() => console.log('Client templates loaded successfully')),
                switchMap(() => of(user)),
                catchError((error) => {
                  console.error('Error loading client templates after login:', error);
                  return of(user);
                }),
              );
          }
          return of(user);
        }),
      );
  }

  ssoLogin(token: string): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/auth/sso-login`, { token })
      .pipe(
        tap((user) => {
          console.log('SSO Login successful, user data:', user);
          localStorage.setItem('name', JSON.stringify(user));
          localStorage.setItem('storename', JSON.stringify(user.warehouse));
          localStorage.setItem('client_id', JSON.stringify(user.client_id));
          this.loadClientSaleData(user.client_id);

          // Store industry from SSO login response
          if (user.industry) {
            console.log('Setting industry from SSO login response:', user.industry);
            this.industryService.setIndustry(user.industry);
          }
          this.accountRegistry.registerActive();

          this.currentUserSubject.next(user);
        }),
        switchMap((user) => {
          if (user.client_id) {
            return this.clientTemplateService
              .getClientTemplates(user.client_id)
              .pipe(
                tap(() => console.log('Client templates loaded successfully')),
                switchMap(() => of(user)),
              );
          }
          return of(user);
        }),
      );
  }

  /**
   * Loads client_sale_data (logo, signature, bank details, terms) for the logged-in client.
   * App init and dashboard init run before login (no client_id yet), so load it here.
   */
  private loadClientSaleData(clientId: number | string | undefined): void {
    this.dynamicTemplateConfigService.resetClientSaleData();
    if (clientId) {
      this.dynamicTemplateConfigService.loadClientSaleData(clientId).subscribe();
    }
  }

  store(): Observable<{ status: string; data: any[] }> {
    return this.http
      .post<any>(`${this.apiUrl}/auth/stores`, {})
      .pipe((warehouses) => warehouses);
  }

  logout(): Observable<any> {
    const credentials = this.getStoredCredentials();
    const headers = credentials
      ? new HttpHeaders({
        Authorization: `Basic ${credentials}`,
      })
      : new HttpHeaders();

    return this.http
      .post<any>(`${this.apiUrl}/auth/logout`, {}, { headers })
      .pipe(
        tap(() => {
          this.signOutActiveAccount();
        }),
        catchError((error) => {
          this.signOutActiveAccount();
          return of({ status: 'logged out locally' });
        }),
      );
  }

  /** Clears this account; another account signed in on this browser (if any) takes over on reload */
  private signOutActiveAccount(): void {
    const nextAccount = this.accountRegistry.removeActive();
    this.clearLocalStorage();
    if (nextAccount) this.accountRegistry.restore(nextAccount.id);
  }

  logoutSync(): void {
    this.clearLocalStorage();
  }

  private clearLocalStorage(): void {
    localStorage.removeItem('name');
    localStorage.removeItem('client_id');
    localStorage.removeItem('auth_credentials');
    localStorage.removeItem('storename');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('punchStatus');
    localStorage.removeItem('shiftStartTime');
    localStorage.removeItem('shiftEndTime');
    this.currentUserSubject.next(null as any);
    this.clientTemplateService.clearTemplates();
    this.dynamicTemplateConfigService.resetClientSaleData();
    this.clearSessionWarning();
    // Clear industry on logout
    this.industryService.clearIndustry();
  }
  logoutSilently() {

    this.accountRegistry.clearStorageKeepingAccounts();
    sessionStorage.clear();
    // localStorage.clear() doesn't reset the in-memory cache of the (root) config service
    this.dynamicTemplateConfigService.resetClientSaleData();

    this.currentUserSubject.next(null as any);

    // Clear industry on silent logout
    this.industryService.clearIndustry();

  }

  /**
   * Get the current industry type
   * @returns Current industry type code
   */
  getIndustry(): number {
    return this.industryService.getIndustry();
  }
}

